import React, { useState, useEffect } from 'react';
import IconSVG from './IconSVG';

interface LikeButtonProps {
  liked: boolean;
  onToggle?: (liked: boolean) => void;
  onCountClick?: () => void;
  count?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  liked,
  onToggle,
  onCountClick,
  count = 0,
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
}) => {
  // Animation state to track like/unlike interactions
  const [animating, setAnimating] = useState(false);
  const [previousLiked, setPreviousLiked] = useState(liked);
  
  // Track when liked state changes to trigger animation
  useEffect(() => {
    if (previousLiked !== liked) {
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 500);
      setPreviousLiked(liked);
      return () => clearTimeout(timer);
    }
  }, [liked, previousLiked]);
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking like button
    
    if (disabled || loading || !onToggle) {
      return;
    }
    
    const newLikedState = !liked;
    onToggle(newLikedState);
    
    setAnimating(true);
    setTimeout(() => setAnimating(false), 500);
  };
  
  // Icon sizes based on button size
  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : 'w-6 h-6';
  
  // Text sizes based on button size
  const textSize = size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base';
  
  // Padding based on button size - Updated with minimum 44px touch targets
  const padding = size === 'sm' ? 'px-3 py-2.5 min-h-[44px]' : size === 'md' ? 'px-4 py-3 min-h-[44px]' : 'px-5 py-3.5 min-h-[48px]';
  
  // Get animation classes for heart icon
  const getAnimationClass = () => {
    if (!animating) return '';
    return liked ? 'animate-heart-like' : 'animate-heart-unlike';
  };
  
  // Get loading state indicator
  const isLoading = loading && !animating;
  
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      className={`flex items-center ${padding} rounded-md transition-colors ${
        liked
          ? 'text-red-600 hover:text-red-700'
          : 'text-gray-500 hover:text-gray-700'
      } ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      aria-label={liked ? 'Unlike' : 'Like'}
    >
      <div className="relative">
        {isLoading ? (
          <div className="animate-spin h-4 w-4 mr-1">
            <IconSVG
              icon="loading"
              className={`${iconSize} text-gray-400`}
            />
          </div>
        ) : (
          <IconSVG
            icon={liked ? 'heart-filled' : 'heart'}
            className={`${iconSize} ${liked ? 'text-red-600' : ''} mr-1 transform transition-transform ${getAnimationClass()}`}
          />
        )}
      </div>
      {count > 0 && onCountClick ? (
        <span
          className={`${textSize} font-medium transition-all duration-300 hover:underline cursor-pointer`}
          onClick={(e) => {
            e.stopPropagation();
            onCountClick();
          }}
        >
          {count}
        </span>
      ) : (
        <span className={`${textSize} font-medium transition-all duration-300`}>
          {count > 0 ? count : liked ? 'Liked' : 'Like'}
        </span>
      )}
    </button>
  );
};

export default LikeButton;